import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';
import { propsDeAtivacao } from '../core/interativo';
import type { EventoDeAtivacao } from '../core/interativo';

/**
 * Bloco de agendamento.
 *
 * A tarja lateral é FIXA por situação — verde confirmado, cinza pendente,
 * vermelho cancelado, azul concluído. Cor de tarja não é decoração: quem lê a
 * agenda de longe reconhece a situação pela faixa antes de ler o texto, e por
 * isso o mapa não muda de tela para tela.
 *
 * Procedimento e profissional aparecem como TAGS por padrão: numa agenda com
 * vários profissionais, "quem atende" é tão operacional quanto "quando".
 */
export type AgendamentoStatus =
    | 'confirmado' | 'pendente' | 'cancelado' | 'concluido' | 'bloqueio' | 'faltou'
    /* Nomes antigos, mantidos para não quebrar telas existentes. */
    | 'aguardando' | 'atendido';

const statusMap: Record<AgendamentoStatus, { bar: string; label: string }> = {
    confirmado: { bar: 'var(--success)', label: 'Confirmado' },
    pendente: { bar: 'var(--neutral-400)', label: 'Pendente' },
    cancelado: { bar: 'var(--danger)', label: 'Cancelado' },
    concluido: { bar: 'var(--info)', label: 'Concluído' },
    bloqueio: { bar: 'var(--neutral-300)', label: 'Bloqueio' },
    /* Falta não é cancelamento: o horário foi perdido sem aviso, e a diferença
       decide cobrança, remarcação e indicador de absenteísmo. Âmbar, para não
       se confundir com o vermelho de cancelado nem com o azul de concluído. */
    faltou: { bar: 'var(--warning)', label: 'Faltou' },
    aguardando: { bar: 'var(--neutral-400)', label: 'Pendente' },
    atendido: { bar: 'var(--info)', label: 'Concluído' },
};

function Etiqueta({ icon, children, tone = 'neutral', sobreCinza = false }: {
    icon?: string; children?: ReactNode; tone?: 'neutral' | 'brand';
    /** No cartão cancelado o fundo já é cinza: a etiqueta neutra some nele. */
    sobreCinza?: boolean;
}) {
    const paleta: CSSProperties = tone === 'brand'
        ? { background: 'var(--surface-brand-soft)', color: 'var(--text-brand)' }
        : { background: sobreCinza ? 'var(--surface)' : 'var(--surface-secondary)', color: 'var(--text-body)' };
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', height: 19, padding: '0 7px', borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 500, ...paleta }}>
            {icon && <Icon name={icon} size={11} />}
            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
        </span>
    );
}

export interface AppointmentCardProps {
    patient?: string;
    procedure?: ReactNode;
    time?: string;
    professional?: ReactNode;
    status?: AgendamentoStatus;
    room?: ReactNode;
    lead?: boolean;
    /** Exibe procedimento, profissional e sala como etiquetas. */
    tags?: boolean;
    compact?: boolean;
    onClick?: (e: EventoDeAtivacao) => void;
    style?: CSSProperties;
}

export function AppointmentCard({
    patient, procedure, time, professional, status = 'confirmado', room,
    lead = false, tags = true, compact = false, onClick, style,
}: AppointmentCardProps) {
    const [hover, setHover] = useState(false);
    const s = statusMap[status] ?? statusMap.confirmado;
    const cancelado = status === 'cancelado';
    const descricao = `${s.label} · ${time ?? ''}${patient ? ` · ${patient}` : ''}`;
    const interacao = propsDeAtivacao(onClick, `Abrir agendamento: ${descricao}`);

    return (
        <article
            {...interacao}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            title={descricao}
            style={{
                display: 'flex', gap: 9, padding: compact ? '6px 9px' : '9px 11px',
                /* Cancelado é CINZA, não desbotado. A opacidade de 65% que havia
                   aqui derrubava o texto do cartão a 2,87:1 — abaixo dos 4,5:1 da
                   norma —, e só voltava a passar em 90%, onde já não parecia
                   cancelado. Opacidade era a ferramenta errada: ela apaga o texto
                   junto com o resto. O cancelamento já se anuncia duas vezes sem
                   ela, na tarja vermelha e no nome riscado; o cinza é a terceira,
                   e não custa legibilidade. */
                background: cancelado ? 'var(--surface-secondary)' : 'var(--surface)',
                border: `1px solid ${hover ? 'var(--border-strong)' : 'var(--border)'}`, borderLeft: `3px solid ${s.bar}`,
                borderRadius: 'var(--radius-sm)', cursor: onClick ? 'pointer' : 'default', minWidth: 0, overflow: 'hidden',
                boxShadow: hover ? 'var(--shadow-sm)' : 'none', transition: 'var(--transition-control)',
                textAlign: 'left', ...style,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{time}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: cancelado ? 'line-through' : 'none' }}>{patient}</span>
                    {lead && <span style={{ flex: '0 0 auto', fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 700, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-brand)' }}>lead</span>}
                </div>

                {!compact && tags && (procedure || professional || room) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                        {procedure && <Etiqueta icon="clipboard-list" tone="brand">{procedure}</Etiqueta>}
                        {professional && <Etiqueta icon="user-round" sobreCinza={cancelado}>{professional}</Etiqueta>}
                        {room && <Etiqueta icon="door-open" sobreCinza={cancelado}>{room}</Etiqueta>}
                    </div>
                )}

                {!compact && !tags && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{procedure}</span>
                        {professional && <><span style={{ color: 'var(--neutral-300)' }}>·</span><span>{professional}</span></>}
                    </div>
                )}
            </div>
        </article>
    );
}
